import { BaseChangeLog } from '@models/next/ChangeLog';
import { InvoiceFields, InvoiceDetailsFields } from '@models/next/Invoice';
import { z } from 'zod';

export const InvoiceLogInvoiceDetails = InvoiceDetailsFields.pick({
  id: true,
  expense_cost: true,
  invoice_name: true,
  unit_num_type: true,
  currency: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
  country: true,
  payer_mark: true,
  debit: true,
  credit: true,
  invoice_name_preparation: true,
  // `updated_at` is omitted by server
  created_at: true,
  invoice: true,
  is_locked: true,
});

export type InvoiceLogInvoiceDetails = z.infer<typeof InvoiceLogInvoiceDetails>;

export const InvoiceLog = z.discriminatedUnion('edited_field', [
  BaseChangeLog.extend({
    edited_field: z.literal('charge_date'),
    before_value: InvoiceFields.shape.charge_date,
    after_value: InvoiceFields.shape.charge_date,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('charge_pay_day'),
    before_value: InvoiceFields.shape.charge_pay_day,
    after_value: InvoiceFields.shape.charge_pay_day,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('client_name'),
    before_value: InvoiceFields.shape.client_name,
    after_value: InvoiceFields.shape.client_name,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('payer_mark'),
    before_value: InvoiceFields.shape.payer_mark,
    after_value: InvoiceFields.shape.payer_mark,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('client_zipcode'),
    before_value: InvoiceFields.shape.client_zipcode,
    after_value: InvoiceFields.shape.client_zipcode,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('client_address1'),
    before_value: InvoiceFields.shape.client_address1,
    after_value: InvoiceFields.shape.client_address1,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('client_address2'),
    before_value: InvoiceFields.shape.client_address2,
    after_value: InvoiceFields.shape.client_address2,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('vessel_voyage'),
    before_value: InvoiceFields.shape.vessel_voyage,
    after_value: InvoiceFields.shape.vessel_voyage,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('transport_route'),
    before_value: InvoiceFields.shape.transport_route,
    after_value: InvoiceFields.shape.transport_route,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('ata'),
    before_value: InvoiceFields.shape.ata,
    after_value: InvoiceFields.shape.ata,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('quantity_packages'),
    before_value: InvoiceFields.shape.quantity_packages,
    after_value: InvoiceFields.shape.quantity_packages,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('customer_no'),
    before_value: InvoiceFields.shape.customer_no,
    after_value: InvoiceFields.shape.customer_no,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('conversion_rate'),
    before_value: InvoiceFields.shape.conversion_rate,
    after_value: InvoiceFields.shape.conversion_rate,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('language'),
    before_value: InvoiceFields.shape.language,
    after_value: InvoiceFields.shape.language,
  }),
  BaseChangeLog.extend({
    edited_field: z.literal('invoice_details'),
    before_value: InvoiceLogInvoiceDetails.array(),
    after_value: InvoiceLogInvoiceDetails.array(),
  }),
]);

export type InvoiceLog = z.infer<typeof InvoiceLog>;

export type InvoiceLogFormatters = {
  [K in InvoiceLog['edited_field']]?: (
    value: Extract<InvoiceLog, { edited_field: K }>['before_value'],
  ) => string;
};
